import { fetchConfig } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

const NOTICE_DISPLAY_DURATION = 5000;
// Checkout stays locked this long after the re-synced cart has re-rendered.
const CART_SETTLE_DELAY = 1000;
const VALIDATED_MARKET_STORAGE_KEY = 'cartMarketValidatedFor';

const CARTMAX_PROPERTY_KEY = '__CartMax_Recommendations';
const CARTMAX_READD_QUERY_KEY = 'cartmax_readd';

const CARTMAX_ATC_SELECTOR = '.cartmax-card-content-right-button.buttondefout';

/**
 * @param {string} url
 * @returns {Promise<boolean>}
 */
async function isProductLinkValid(url) {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.status !== 404;
  } catch (error) {
    return true;
  }
}

/**
 * Resolves the equivalent product handle for `item` in the current storefront language, via the
 * per-item mapping rendered server-side into `window.CartLanguageProductMap` from each item's own
 * `custom.product_groups_per_market` metaobject reference (DAO-40). Items with no mapped group
 * (e.g. the membership product) fall back to the existing remove-and-notify behavior.
 * @param {{key: string}} item
 * @returns {string | null}
 */
function resolveEquivalentHandle(item) {
  const entry = window.CartLanguageProductMap?.[item.key];
  if (!entry) return null;

  const currentLanguage = (window.Shopify?.locale || '').toLowerCase().split('-')[0];
  return entry[currentLanguage] || null;
}

/**
 * Swaps an invalid cart item for its equivalent product in the current language, carrying the
 * quantity and line-item properties over (AC #4 of DAO-40).
 * @param {{key: string, quantity: number, properties: Record<string, string> | null}} item
 * @param {string} targetHandle
 * @returns {Promise<string | null>} The swapped-to product's title, or null if the swap failed.
 */
async function swapItemForEquivalent(item, targetHandle) {
  try {
    const productResponse = await fetch(`/products/${targetHandle}.js`);
    if (!productResponse.ok) return null;

    const product = await productResponse.json();
    const targetVariant = product.variants?.find((variant) => variant.available) || product.variants?.[0];
    if (!targetVariant) return null;

    const removeResponse = await fetch(
      Theme.routes.cart_change_url,
      fetchConfig('json', { body: JSON.stringify({ id: item.key, quantity: 0 }) })
    );
    if (!removeResponse.ok) return null;

    const addResponse = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({
          items: [
            {
              id: targetVariant.id,
              quantity: item.quantity,
              properties: { ...(item.properties || {}), _language: window.Shopify?.locale || '' },
            },
          ],
        }),
      })
    );

    const swapSucceeded = addResponse.ok;
    if (!swapSucceeded) {
      console.error('Swap add failed for', targetHandle, '— item was already removed from cart.');
    }
    return swapSucceeded ? product.title : null;
  } catch (error) {
    console.error('Failed to swap cart item to its language equivalent:', error);
    return null;
  }
}

/**
 * Parses a cart item's CartMax bundle metadata, if it has any.
 * @param {{properties?: Record<string, string> | null}} item
 * @returns {{configId: string | number, isMain: boolean} | null}
 */
function parseCartMaxConfig(item) {
  const raw = item.properties?.[CARTMAX_PROPERTY_KEY];
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return {
      configId: parsed?.config_id,
      isMain: Boolean(parsed?.ai?.is_main),
    };
  } catch (error) {
    console.error('Failed to parse CartMax bundle properties:', error);
    return null;
  }
}

/**
 * Removes every CartMax bundle item from the cart, grouped and cleared together per `config_id`
 * so a market switch never leaves a partial bundle behind (DAO-40). Returns a "re-add" URL to the
 * bundle's main product — found by matching a cart item's own `ai.is_main` flag — but only when
 * there's exactly one bundle in the cart; with more than one at once, everything still gets
 * removed, just without a specific link, rather than guessing which bundle a single link should
 * point back to.
 *
 * The main item's own `.url` is its *old*-market product, market-locked the same way any other
 * product here is — switching market doesn't change which product a cart item points to, so
 * using it directly 404s exactly like it would for a non-bundle item. Resolved via the same
 * `resolveEquivalentHandle()` used for the swap path instead, falling back to the item's own URL
 * only when it isn't one of our mapped language-group products to begin with (e.g. a universal
 * add-on that isn't market-restricted, where its own URL is already correct).
 * @param {any[]} cartItems
 * @returns {Promise<{ removedAny: boolean, reAddUrl: string | null }>}
 */
async function removeCartMaxBundles(cartItems) {
  const cartmaxItems = cartItems
    .map((item) => ({ item, config: parseCartMaxConfig(item) }))
    .filter(({ config }) => config);

  if (!cartmaxItems.length) return { removedAny: false, reAddUrl: null };

  const configIds = [...new Set(cartmaxItems.map(({ config }) => config.configId))];
  let reAddUrl = null;

  if (configIds.length === 1) {
    const mainEntry = cartmaxItems.find(({ config }) => config.isMain);
    if (mainEntry) {
      const targetHandle = resolveEquivalentHandle(mainEntry.item);
      reAddUrl = targetHandle ? `/products/${targetHandle}` : mainEntry.item.url || null;
    }
  }

  for (const { item } of cartmaxItems) {
    try {
      await fetch(
        Theme.routes.cart_change_url,
        fetchConfig('json', { body: JSON.stringify({ id: item.key, quantity: 0 }) })
      );
    } catch (error) {
      console.error('Failed to remove CartMax bundle item:', error);
    }
  }

  return { removedAny: true, reAddUrl };
}

/**
 * @param {string} productUrl
 * @returns {string}
 */
function buildCartMaxReAddUrl(productUrl) {
  const url = new URL(productUrl, window.location.origin);
  url.searchParams.set(CARTMAX_READD_QUERY_KEY, '1');
  return `${url.pathname}${url.search}`;
}

/**
 * @param {string | null} reAddUrl
 */
function showCartMaxRemovedNotice(reAddUrl) {
  const notice = document.createElement('div');
  notice.className = 'cart-market-notice';
  notice.setAttribute('role', 'status');
  notice.setAttribute('aria-live', 'polite');

  if (reAddUrl) {
    // `_html` translation: pre-approved markup with a `{{ link }}` placeholder, same convention
    // as e.g. `content.shipping_policy_html` elsewhere in this theme — not user input, safe to
    // set as innerHTML.
    const template =
      Theme.translations.cart_bundle_removed_html ||
      'Your bundle was removed from the cart since its pricing couldn’t be updated for your new region. Please <a href="{{ link }}">re-add it</a> to continue.';
    notice.innerHTML = template.replace('{{ link }}', buildCartMaxReAddUrl(reAddUrl));
  } else {
    notice.textContent =
      Theme.translations.cart_bundle_removed ||
      'Your bundle was removed from the cart since its pricing couldn’t be updated for your new region. Please re-add it to continue.';
  }

  document.body.appendChild(notice);

  setTimeout(() => {
    notice.classList.add('cart-market-notice--hiding');
    notice.addEventListener('transitionend', () => notice.remove(), { once: true });
  }, NOTICE_DISPLAY_DURATION * 2);
}

function autoClickCartMaxButtonIfFlagged() {
  const params = new URLSearchParams(window.location.search);
  if (params.get(CARTMAX_READD_QUERY_KEY) !== '1') return;

  params.delete(CARTMAX_READD_QUERY_KEY);
  const query = params.toString();
  window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);

  if (!CARTMAX_ATC_SELECTOR) return;

  const ATTEMPT_INTERVAL_MS = 300;
  const MAX_ATTEMPTS = 20;
  let attempts = 0;

  const interval = setInterval(() => {
    attempts += 1;
    const button = document.querySelector(CARTMAX_ATC_SELECTOR);

    if (button instanceof HTMLElement) {
      clearInterval(interval);
      button.click();
    } else if (attempts >= MAX_ATTEMPTS) {
      clearInterval(interval);
      console.warn('CartMax bundle button not found for auto-re-add; CARTMAX_ATC_SELECTOR may need updating.');
    }
  }, ATTEMPT_INTERVAL_MS);
}

/**
 * @param {string} currentCountry
 * @returns {Promise<boolean>} Whether the cart was changed.
 */
async function removeItemsNotAvailableInMarket(currentCountry) {
  const cart = await fetch('/cart.js').then((response) => response.json());
  let cartChanged = false;

  if (cart.items?.length) {
    const { removedAny: cartMaxRemoved, reAddUrl: cartMaxReAddUrl } = await removeCartMaxBundles(cart.items);

    const remainingItems = cart.items.filter((item) => !parseCartMaxConfig(item));
    const invalidItems = (
      await Promise.all(remainingItems.map(async (item) => ((await isProductLinkValid(item.url)) ? null : item)))
    ).filter(Boolean);

    const switchedTitles = /** @type {string[]} */ ([]);
    const removedTitles = /** @type {string[]} */ ([]);

    for (const item of invalidItems) {
      const targetHandle = resolveEquivalentHandle(item);
      const swappedTitle = targetHandle ? await swapItemForEquivalent(item, targetHandle) : null;

      if (swappedTitle) {
        switchedTitles.push(swappedTitle);
        continue;
      }

      try {
        const response = await fetch(
          Theme.routes.cart_change_url,
          fetchConfig('json', { body: JSON.stringify({ id: item.key, quantity: 0 }) })
        );
        if (response.ok) removedTitles.push(item.product_title || item.title);
      } catch (error) {
        console.error('Failed to remove out-of-market cart item:', error);
      }
    }

    if (switchedTitles.length || removedTitles.length || cartMaxRemoved) {
      cartChanged = true;
      const updatedCart = await fetch('/cart.js').then((response) => response.json());

      await Promise.allSettled(
        [...document.querySelectorAll('cart-items-component')].map((cartItems) => {
          const sectionId = /** @type {HTMLElement} */ (cartItems).dataset.sectionId;
          return sectionId && sectionRenderer.renderSection(sectionId, { cache: false });
        })
      );

      const cartIcon = /** @type {any} */ (document.querySelector('cart-icon'));
      cartIcon?.renderCartBubble?.(updatedCart.item_count, false, false);

      if (switchedTitles.length) showNotice(switchedTitles, 'switched');
      if (removedTitles.length) showNotice(removedTitles, 'removed');
      if (cartMaxRemoved) showCartMaxRemovedNotice(cartMaxReAddUrl);
    }
  }

  sessionStorage.setItem(VALIDATED_MARKET_STORAGE_KEY, currentCountry);
  return cartChanged;
}

/**
 * The pre-navigation lock (cart-market-lock.js) dies with the page, so re-lock the cart here while
 * its items are swapped to the new market, and keep it locked CART_SETTLE_DELAY past the re-render.
 */
async function syncCartToMarket() {
  const currentCountry = window.Shopify?.country || '';
  if (sessionStorage.getItem(VALIDATED_MARKET_STORAGE_KEY) === currentCountry) return;

  window.lockCartForMarketSwitch?.({ cartOnly: true });
  try {
    if (await removeItemsNotAvailableInMarket(currentCountry)) {
      await new Promise((resolve) => setTimeout(resolve, CART_SETTLE_DELAY));
    }
  } finally {
    window.unlockCartForMarketSwitch?.();
  }
}

/**
 * @param {string[]} titles
 * @param {'removed' | 'switched'} action
 */
function showNotice(titles, action) {
  const singleKey = action === 'switched' ? 'cart_market_item_switched' : 'cart_market_item_removed';
  const pluralKey = action === 'switched' ? 'cart_market_items_switched' : 'cart_market_items_removed';
  const singleFallback =
    action === 'switched'
      ? 'Switched "{{ title }}" to the version for this market.'
      : 'Removed "{{ title }}" — not available in this market.';
  const pluralFallback =
    action === 'switched'
      ? 'Switched {{ count }} items to the version for this market.'
      : 'Removed {{ count }} items — not available in this market.';

  const message =
    titles.length === 1
      ? (Theme.translations[singleKey] || singleFallback).replace('{{ title }}', titles[0])
      : (Theme.translations[pluralKey] || pluralFallback).replace('{{ count }}', String(titles.length));

  const notice = document.createElement('div');
  notice.className = 'cart-market-notice';
  notice.setAttribute('role', 'status');
  notice.setAttribute('aria-live', 'polite');
  notice.textContent = message;

  document.body.appendChild(notice);

  setTimeout(() => {
    notice.classList.add('cart-market-notice--hiding');
    notice.addEventListener('transitionend', () => notice.remove(), { once: true });
  }, NOTICE_DISPLAY_DURATION);
}

syncCartToMarket();
autoClickCartMaxButtonIfFlagged();
